import { addMinutes, format, parseISO } from 'date-fns'
import { useId } from 'react'
import {
  Area,
  AreaChart,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts'

import ArrowDown from '../../../assets/images/icons/arrow-down.svg?react'
import ArrowUp from '../../../assets/images/icons/arrow-up.svg?react'
import IndexIcon from '../../../assets/images/icons/index-icon.svg?react'
import ChartTooltip from '../../../shared/components/ChartTooltip'
import type { Dataset, IndexProps } from '../../../store/types/dashboard'
import SpinLoader from '../../../utils/loaders/spin'

type Row = { time: string; value: number }

function buildRows(dataset: Dataset[]): Row[] {
  return [...dataset]
    .sort(
      (a, b) =>
        new Date(a.index_date).getTime() - new Date(b.index_date).getTime(),
    )
    .map((item) => ({
      // Kept as an ISO string (timezone-shifted like the previous version)
      // so the axis/tooltip can format it directly with date-fns, instead
      // of pre-formatting to a fixed 'dd/LL/yyyy HH:mm' string.
      time: item.index_date
        ? addMinutes(
            new Date(item.index_date),
            new Date(item.index_date).getTimezoneOffset(),
          ).toISOString()
        : '',
      value: parseFloat(item.prisync_index),
    }))
}

export function LoadingIndexWidget({ compact = false }: { compact?: boolean }) {
  return (
    <div className="flex flex-col border border-solid border-gray-200 rounded-lg shadow h-full">
      <div
        className={`flex flex-row items-center gap-2 border-b border-solid border-gray-100 ${
          compact ? 'px-4 sm:px-5 py-4' : 'px-4 sm:px-6 pt-5 pb-4'
        }`}
      >
        <IndexIcon />
        <span
          className={
            compact
              ? 'font-medium text-lg text-gray-900'
              : 'font-semibold text-2xl text-gray-900'
          }
        >
          Index
        </span>
      </div>
      <div className="flex flex-col items-center justify-center flex-1 pb-6">
        <SpinLoader />
        <div className="font-medium text-sm text-gray-700 pb-1 pt-4">
          Calculating...
        </div>
        <p className="font-normal text-xs text-gray-500">
          Calculation will be completed within 20 minutes.
        </p>
      </div>
    </div>
  )
}

type IndexWidgetProps = IndexProps & {
  showTrend?: boolean
  // Plain-title, no-icon, no-number header matching the "Price Index Trend"
  // reference card — used on the dashboard, where the chart is the point.
  compact?: boolean
}

export default function IndexWidget({
  average,
  dataset,
  index,
  showTrend = false,
  compact = false,
}: IndexWidgetProps) {
  const rows = buildRows(dataset)
  const chartIndex = parseFloat(index === null ? '' : index)
  const chartAverage = parseFloat(average === null ? '' : average)
  const canShowTrend =
    showTrend &&
    !Number.isNaN(chartIndex) &&
    !Number.isNaN(chartAverage) &&
    chartIndex !== chartAverage
  const isHigher = chartIndex > chartAverage
  const gradientId = `index-area-fill-${useId()}`

  return (
    <div className="flex flex-col border border-solid border-gray-200 rounded-lg shadow h-full">
      <div
        className={`flex flex-row items-center justify-between gap-4 border-b border-solid border-gray-100 ${
          compact ? 'px-4 sm:px-5 py-4' : 'px-4 sm:px-6 pt-5 pb-4'
        }`}
      >
        <div className="flex flex-row items-center gap-2">
          <IndexIcon />
          <span
            className={
              compact
                ? 'font-medium text-lg text-gray-900'
                : 'font-semibold text-2xl text-gray-900'
            }
          >
            Index
          </span>
          {compact && (
            <span className="font-normal text-lg text-gray-900">{index}</span>
          )}
        </div>
        {!compact && (
          <div className="flex flex-col items-end">
            <span className="font-semibold text-3xl text-gray-900">
              {index}
            </span>
            {canShowTrend &&
              (isHigher ? (
                <div className="flex flex-row items-center mt-1">
                  <ArrowUp />
                  <span className="pl-1.5 font-medium text-sm text-success-700">
                    {(((chartIndex - chartAverage) / chartIndex) * 100).toFixed(
                      0,
                    )}
                    %
                  </span>
                  <span className="pl-2 font-medium text-sm text-gray-500 whitespace-nowrap">
                    higher than av.
                  </span>
                </div>
              ) : (
                <div className="flex flex-row items-center mt-1">
                  <ArrowDown />
                  <span className="pl-1.5 font-medium text-sm text-red-500">
                    {(
                      ((chartAverage - chartIndex) / chartAverage) *
                      100
                    ).toFixed(0)}
                    %
                  </span>
                  <span className="pl-2 font-medium text-sm text-gray-500 whitespace-nowrap">
                    lower than av.
                  </span>
                </div>
              ))}
          </div>
        )}
      </div>
      <div
        className={`flex-1 pr-2 sm:pr-4 pl-1 ${
          compact ? 'min-h-[110px] pt-2 pb-3' : 'min-h-[220px] pt-4 pb-4'
        }`}
      >
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={rows}
            margin={{ top: 8, right: 30, bottom: 0, left: 20 }}
          >
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#12B76A" stopOpacity={0.25} />
                <stop offset="95%" stopColor="#12B76A" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="2 4"
              stroke="#D0D5DD"
              vertical={false}
            />
            <XAxis
              dataKey="time"
              tickLine={false}
              axisLine={false}
              interval="preserveStartEnd"
              minTickGap={40}
              tick={{ fill: '#667085', fontSize: 12, dy: 8 }}
              tickFormatter={(value: string) =>
                value ? format(parseISO(value), 'MMM d') : ''
              }
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tick={{ fill: '#667085', fontSize: 12 }}
              width={48}
              label={{
                value: 'Index',
                angle: -90,
                position: 'insideLeft',
                style: { fill: '#667085', fontSize: 12, textAnchor: 'middle' },
              }}
            />
            <RechartsTooltip
              content={(props) => (
                <ChartTooltip
                  {...props}
                  label={
                    typeof props.label === 'string' && props.label
                      ? format(parseISO(props.label), 'MMM d, h a')
                      : props.label
                  }
                  formatValue={(value) => Number(value).toFixed(2)}
                />
              )}
              cursor={{ stroke: '#12B76A', strokeOpacity: 0.2 }}
              wrapperStyle={{ zIndex: 50 }}
            />
            <ReferenceLine
              y={100}
              stroke="#98A2B3"
              strokeDasharray="4 4"
              label={{
                value: '100',
                position: 'right',
                fill: '#667085',
                fontSize: 11,
              }}
            />
            <Area
              type="monotone"
              dataKey="value"
              name="Index"
              stroke="#12B76A"
              strokeWidth={2.5}
              fill={`url(#${gradientId})`}
              isAnimationActive={false}
              dot={false}
              activeDot={{ r: 4, fill: '#12B76A', stroke: '#12B76A' }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}
